import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import {
  UserProfile,
  UserRole,
  EmployeeRole,
} from '../../../shared/interfaces/role.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Row model extends backend UserRole with UI-only assigned flag
interface UserRoleRow extends UserRole {
  assigned: boolean;
}

@Component({
  selector: 'app-edit-user-role',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    LoaderComponent,
    RouterLink,
  ],
  templateUrl: './edit-user-role.component.html',
  styleUrls: [
    './edit-user-role.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class EditUserRoleComponent implements OnDestroy {
  private _snackBar = inject(MatSnackBar);
  private _liveAnnouncer = inject(LiveAnnouncer);
  private destroy$ = new Subject<void>();

  myroles: any = {}; // Change from array to object
  externalId: string = '';
  userName: string = '';
  allRoles: UserRoleRow[] = [];
  userRoles: number[] = [];
  existingUserRoleAssignments: EmployeeRole[] = []; // Track existing role assignments from backend
  hasExistingRoles: boolean = false; // Flag to track if user has existing roles

  // Loading coordination flags
  private rolesLoaded: boolean = false;
  private employeeRolesLoaded: boolean = false;

  dataSource: MatTableDataSource<UserRoleRow> =
    new MatTableDataSource<UserRoleRow>();
  displayedColumns: string[] = ['name', 'description', 'assigned'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private sanitizer: DomSanitizer
  ) {}

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.loaderService.showLoader();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const extId = params['id'];
      //  console.log('Route params:', params);
      this.externalId = extId;

      // Get roles assigned to this user
      this.loadUserRoles();
    });

    this.loadRoles();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadRoles() {
    // Get permissions using the service method
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'policies',
      this.storageService
    );

    if (permissions) {
      // Store permissions in myroles
      this.myroles = permissions;

      // Check write permission and redirect if needed
      this.rolesService.checkWritePermission(
        'policies',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/users/list',
        'No tienes permisos para editar los roles de un usuario'
      );
    } else {
      //  console.log('No user roles found in storage');
    }
  }

  loadUserRoles() {
    // Reset flags when reloading
    this.rolesLoaded = false;
    this.employeeRolesLoaded = false;
    this.loadAllRoles();
    this.loadEmployeeRoles();
  }

  private loadAllRoles() {
    this.rolesService
      .getAllRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (roles) => {
          this.allRoles = roles.map((role) => ({ ...role, assigned: false }));
          this.rolesLoaded = true;
          this.markAssigned();
          this.finalizeLoading();
        },
        error: (error) => {
          console.error('Error loading all roles:', error);
          this.openSnackBar('Error al cargar los roles disponibles');
          this.rolesLoaded = true; // allow finalize to proceed
          this.finalizeLoading();
        },
      });
  }

  private loadEmployeeRoles() {
    this.rolesService
      .getEmployeeRoles(this.externalId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (employeeRoles) => {
          // Some backends might return null instead of [] when no roles
          if (!employeeRoles || !Array.isArray(employeeRoles)) {
            //console.log('Employee roles response empty/null, treating as none');
            this.hasExistingRoles = false;
            this.existingUserRoleAssignments = [];
            this.userRoles = [];
          } else {
            // console.log('Employee roles:', employeeRoles);
            this.hasExistingRoles = employeeRoles.length > 0;
            this.existingUserRoleAssignments = employeeRoles;
            this.userRoles = employeeRoles.map(
              (er) => er.role_details?.id || er.role
            );
          }
          this.employeeRolesLoaded = true;
          this.markAssigned();
          this.finalizeLoading();
        },
        error: (error) => {
          //  console.log('User has no roles or error occurred:', error);
          this.hasExistingRoles = false;
          this.existingUserRoleAssignments = [];
          this.userRoles = []; // none assigned
          this.employeeRolesLoaded = true;
          this.markAssigned();
          this.finalizeLoading();
        },
      });
  }

  private markAssigned() {
    if (!this.allRoles || this.allRoles.length === 0) return;
    // Update assigned flag based on current userRoles list
    this.allRoles.forEach((role) => {
      role.assigned = this.userRoles.includes(role.id);
    });
    this.dataSource.data = this.allRoles;
  }

  private finalizeLoading() {
    if (this.rolesLoaded && this.employeeRolesLoaded) {
      this.loaderService.hideLoader();
    }
  }

  toggleRoleAssignment(role: UserRoleRow) {
    role.assigned = !role.assigned;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    if (filterValue) {
      this.dataSource.filter = filterValue.trim().toLowerCase();
    } else {
      this.dataSource.filter = '';
    }

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  saveRoleAssignments() {
    this.loaderService.showLoader();

    // Get IDs of roles that should be assigned
    const assignedRoleIds = this.allRoles
      .filter((r) => r.assigned)
      .map((r) => r.id);

    // console.log('Roles to assign:', assignedRoleIds);

    // Counter to track completed operations
    let completedOperations = 0;
    let totalOperations = 0;
    let errorOccurred = false;

    // Calculate total operations: roles to add + roles to remove
    const rolesToAdd = assignedRoleIds.filter(
      (roleId) =>
        !this.existingUserRoleAssignments.some(
          (a) => (a.role_details?.id || a.role) === roleId
        )
    );

    const rolesToRemove = this.hasExistingRoles
      ? this.existingUserRoleAssignments.filter(
          (assignment) =>
            !assignedRoleIds.includes(
              assignment.role_details?.id || assignment.role
            ) && assignment.id
        )
      : [];

    totalOperations = rolesToAdd.length + rolesToRemove.length;

    // If no changes, just go back
    if (totalOperations === 0 && assignedRoleIds.length > 0) {
      this.loaderService.hideLoader();
      this.openSnackBar('No se detectaron cambios en los roles');
      this.router.navigate(['/home/users/list']);
      return;
    }

    // Case where we're removing all roles from a user
    if (assignedRoleIds.length === 0 && rolesToRemove.length > 0) {
      // Process role removals
      this.removeAllRoles(rolesToRemove);
      return;
    }

    // Normal case: If no roles selected and we're not removing any, show message and return
    if (assignedRoleIds.length === 0) {
      this.loaderService.hideLoader();
      this.openSnackBar('Debe seleccionar al menos un rol');
      return;
    }

    // Process each role to add
    rolesToAdd.forEach((roleId) => {
      this.rolesService
        .postEmployeeRole({
          external_id: this.externalId,
          role: roleId,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            completedOperations++;
            this.checkCompletion(
              totalOperations,
              completedOperations,
              errorOccurred
            );
          },
          error: (error) => {
            console.error(`Error adding role ${roleId}:`, error);
            errorOccurred = true;
            completedOperations++;
            this.checkCompletion(
              totalOperations,
              completedOperations,
              errorOccurred
            );
          },
        });
    });

    // Handle roles that need to be removed (but not all of them)
    if (this.hasExistingRoles) {
      rolesToRemove.forEach((assignment) => {
        // Use deleteEmployeeRole to remove the role
        if (assignment.id) {
          this.rolesService
            .deleteEmployeeRole(assignment.id, this.externalId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                //    console.log(
                //      `Role ${
                //       assignment.role_details?.id || assignment.role
                //      } removed successfully`
                //   );
                completedOperations++;
                this.checkCompletion(
                  totalOperations,
                  completedOperations,
                  errorOccurred
                );
              },
              error: (error) => {
                console.error(
                  `Error removing role ${
                    assignment.role_details?.id || assignment.role
                  }:`,
                  error
                );
                errorOccurred = true;
                completedOperations++;
                this.checkCompletion(
                  totalOperations,
                  completedOperations,
                  errorOccurred
                );
              },
            });
        } else {
          // If for some reason there's no ID, just count it as completed
          completedOperations++;
          this.checkCompletion(
            totalOperations,
            completedOperations,
            errorOccurred
          );
        }
      });
    }

    // If there are no roles to add or remove, complete the operation
    if (totalOperations === 0) {
      this.loaderService.hideLoader();
      this.openSnackBar('Roles actualizados correctamente');
      this.router.navigate(['/home/users/list']);
    }
  }

  // New helper method to handle removing all roles
  removeAllRoles(rolesToRemove: EmployeeRole[]) {
    let completedOperations = 0;
    let errorOccurred = false;
    const totalToRemove = rolesToRemove.length;

    rolesToRemove.forEach((assignment) => {
      // Use deleteEmployeeRole to remove the role
      if (assignment.id) {
        this.rolesService
          .deleteEmployeeRole(assignment.id, this.externalId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              //  console.log(
              //   `Role ${
              //      assignment.role_details?.id || assignment.role
              //     } removed successfully`
              //   );
              completedOperations++;
              if (completedOperations >= totalToRemove) {
                this.loaderService.hideLoader();
                if (errorOccurred) {
                  this.openSnackBar(
                    'Se produjo un error al eliminar algunos roles'
                  );
                } else {
                  this.openSnackBar(
                    'Se han eliminado todos los roles del usuario'
                  );
                  this.router.navigate(['/home/users/list']);
                }
              }
            },
            error: (error) => {
              console.error(
                `Error removing role ${
                  assignment.role_details?.id || assignment.role
                }:`,
                error
              );
              errorOccurred = true;
              completedOperations++;
              if (completedOperations >= totalToRemove) {
                this.loaderService.hideLoader();
                this.openSnackBar(
                  'Se produjo un error al eliminar algunos roles'
                );
              }
            },
          });
      } else {
        // If for some reason there's no ID, just count it as completed
        completedOperations++;
        if (completedOperations >= totalToRemove) {
          this.loaderService.hideLoader();
          if (errorOccurred) {
            this.openSnackBar('Se produjo un error al eliminar algunos roles');
          } else {
            this.openSnackBar('Se han eliminado todos los roles del usuario');
            this.router.navigate(['/home/users/list']);
          }
        }
      }
    });
  }

  // Helper method to check if all operations are complete
  checkCompletion(total: number, completed: number, hasError: boolean) {
    if (completed >= total) {
      this.loaderService.hideLoader();

      if (hasError) {
        this.openSnackBar('Se produjo un error al actualizar algunos roles');
      } else {
        this.openSnackBar('Roles actualizados correctamente');
        this.router.navigate(['/home/users/list']);
      }
    }
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, 'Cerrar', {
      duration: 5000,
    });
  }

  goBack() {
    this.router.navigate(['/home/users/list']);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
